import React from 'react';
import { useTranslation } from 'react-i18next';

import { Callout, Tab, TabsRow, Text, type TableColumn } from '../../../../../components';
import {
  Wrap,
  GuidePanel,
  Steps,
  Step,
  Settings,
  SettingsHeading,
  SettingsDescription,
  TableScroll,
  Mono,
  SettingsTable,
} from './ClientGuide.styles';
import type { MailClientSettings } from '../../../api/client';

export type ClientGuideProps = {
  /** The brand's mail domain: the root of the server hosts. */
  domain: string;
  /** The provider's own hosts and ports, when the service has answered with them. Absent (the mock
   *  source, or before and after a failed read) the table shows the fixed rows below. */
  settings?: MailClientSettings;
};

/** The five clients we give a path for. */
type ClientTab = 'apple' | 'outlook' | 'thunderbird' | 'mobile' | 'gmail';
const TAB_IDS: readonly ClientTab[] = ['apple', 'outlook', 'thunderbird', 'mobile', 'gmail'];
const isTab = (value: string | undefined): value is ClientTab => TAB_IDS.includes(value as ClientTab);

/** One row of the server-settings table. Protocol names and security schemes are machine values. */
type ServerRow = { id: string; protocol: string; server: string; port: string; security: string };

/**
 * The mock source's rows: IMAP 993 and SMTP 465, both SSL/TLS, on `mail.{domain}` (the prototype).
 * The live source passes the provider's own settings (`settings`), which win: a mismatch between
 * what we display and what the server accepts is a silent failure the operator cannot diagnose from
 * their mail client's error. These rows are also what shows if that read fails.
 */
const fixedServerRows = (domain: string): ServerRow[] => [
  { id: 'imap', protocol: 'IMAP', server: `mail.${domain}`, port: '993', security: 'SSL/TLS' },
  { id: 'smtp', protocol: 'SMTP', server: `mail.${domain}`, port: '465', security: 'SSL/TLS' },
];

/** The rows the provider's settings describe (both protocols share its one security scheme). */
const serverRows = (domain: string, settings: MailClientSettings | undefined): ServerRow[] => {
  if (!settings) return fixedServerRows(domain);
  const security = settings.security ?? 'SSL/TLS';
  return [
    { id: 'imap', protocol: 'IMAP', server: settings.imapHost, port: String(settings.imapPort), security },
    { id: 'smtp', protocol: 'SMTP', server: settings.smtpHost, port: String(settings.smtpPort), security },
  ];
};

/**
 * "Set it up in your mail app": five client tabs with numbered steps under the active one, then
 * the server settings for the client that asks. The tab is local state on this component, so a
 * toggle elsewhere on the page (the password eye) cannot reset it.
 *
 * A11y: `TabsRow` carries `role="tablist"` and each steps block is a `tabpanel`. The shared `Tab`
 * renders a plain button with no `role="tab"` / `aria-selected` passthrough; giving it those is a
 * claimed edit to the shared component, not this scenario's to make.
 */
export const ClientGuide = ({ domain, settings }: ClientGuideProps): React.ReactElement => {
  const { t } = useTranslation();
  const [tab, setTab] = React.useState<ClientTab>('apple');
  const onTab = React.useCallback((event: React.MouseEvent<HTMLElement>): void => {
    const id = event.currentTarget.dataset.id;
    if (isTab(id)) setTab(id);
  }, []);

  const columns: TableColumn<ServerRow>[] = [
    { id: 'protocol', header: t('Protocol'), cell: (row) => <Mono>{row.protocol}</Mono> },
    { id: 'server', header: t('Server'), cell: (row) => <Mono>{row.server}</Mono> },
    { id: 'port', header: t('Port'), cell: (row) => <Mono>{row.port}</Mono> },
    { id: 'security', header: t('Security'), cell: (row) => <Mono>{row.security}</Mono> },
  ];

  return (
    <Wrap>
      <TabsRow role="tablist" aria-label={t('Mail app')}>
        <Tab id="apple" label={t('Apple Mail')} active={tab === 'apple'} onClick={onTab} />
        <Tab id="outlook" label={t('Outlook')} active={tab === 'outlook'} onClick={onTab} />
        <Tab id="thunderbird" label={t('Thunderbird')} active={tab === 'thunderbird'} onClick={onTab} />
        <Tab id="mobile" label={t('iPhone / Android')} active={tab === 'mobile'} onClick={onTab} />
        <Tab id="gmail" label={t('Gmail')} active={tab === 'gmail'} onClick={onTab} />
      </TabsRow>

      {tab === 'apple' ? (
        <Steps role="tabpanel" data-testid="mailbox-guide-apple">
          <Step>
            <Text
              text="Open Mail, then Settings, then Accounts, then Add Account, then Other Mail Account."
              tag="span"
            />
          </Step>
          <Step>
            <Text text="Enter your name, the address and the password." tag="span" />
          </Step>
          <Step>
            {/* Not automatic: the zone publishes no records for mail clients to configure themselves
                (a live mailbox proved it), so the operator enters the settings from the table below. */}
            <Text text="Enter the server settings shown below, then sign in." tag="span" />
          </Step>
        </Steps>
      ) : null}

      {tab === 'outlook' ? (
        <Steps role="tabpanel" data-testid="mailbox-guide-outlook">
          <Step>
            <Text text="Open File, then Add Account." tag="span" />
          </Step>
          <Step>
            <Text text="Type the address and click Connect." tag="span" />
          </Step>
          <Step>
            <Text text="Enter the server settings shown below, then sign in." tag="span" />
          </Step>
        </Steps>
      ) : null}

      {tab === 'thunderbird' ? (
        <Steps role="tabpanel" data-testid="mailbox-guide-thunderbird">
          <Step>
            <Text text="Open Account Settings, then Account Actions, then Add Mail Account." tag="span" />
          </Step>
          <Step>
            <Text text="Enter your name, the address and the password, then click Done." tag="span" />
          </Step>
        </Steps>
      ) : null}

      {tab === 'mobile' ? (
        <Steps role="tabpanel" data-testid="mailbox-guide-mobile">
          <Step>
            <Text
              text="iPhone: open Settings, then Apps, then Mail, then Mail Accounts, then Add Account, then Other."
              tag="span"
            />
          </Step>
          <Step>
            <Text text="Android (Gmail app): open Settings, then Add account, then Other." tag="span" />
          </Step>
          <Step>
            <Text text="Enter the address, the password and the server settings shown below." tag="span" />
          </Step>
        </Steps>
      ) : null}

      {tab === 'gmail' ? (
        // The warning is NOT a step: it is a caveat about the whole Gmail route, so it sits ABOVE
        // the numbered list, outside the <ol> (inside it, the li's marker rendered on the callout's
        // corner). The panel wrapper carries the tabpanel role, since it now has two children.
        <GuidePanel role="tabpanel" data-testid="mailbox-guide-gmail">
          {/* Said plainly, because it is the one client where a real mailbox is a worse fit than
              forwarding, and operators should hear it from us rather than from Google. */}
          <Callout
            tone="warning"
            text="Gmail on the web can only pull this mailbox over POP3, which downloads mail and leaves the sent copy behind. The Gmail mobile app does support IMAP; use that, or another client, for the full mailbox."
          />
          <Steps>
            <Step>
              <Text
                text="In Gmail on the web, open Settings, then Accounts and Import, then Check mail from other accounts."
                tag="span"
              />
            </Step>
            <Step>
              <Text text="Enter the address, then the server settings below with the password." tag="span" />
            </Step>
          </Steps>
        </GuidePanel>
      ) : null}

      <Settings>
        <SettingsHeading text="Server settings" tag="span" />
        <SettingsDescription
          text="Your mail client needs these to connect: it will not find them by itself."
          tag="span"
        />
        <TableScroll data-testid="mailbox-server-table">
          <SettingsTable
            columns={columns}
            rows={serverRows(domain, settings)}
            rowKey={(row) => row.id}
            label={t('Server settings')}
          />
        </TableScroll>
      </Settings>
    </Wrap>
  );
};

export default ClientGuide;
